import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, CheckCircle2, XCircle, PlayCircle } from "lucide-react";

export default function ModuleDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/content/modules/${id}`).then((r) => setM(r.data)).catch(() => {});
  }, [id]);

  if (!m) return <div className="text-slate-500" data-testid="module-loading">Loading module…</div>;

  const quiz = m.quiz || [];
  const allAnswered = quiz.every((_, i) => answers[i] !== undefined);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = quiz.map((_, i) => answers[i] ?? -1);
      const { data } = await api.post("/progress/quiz", { module_id: id, answers: payload });
      setResult(data);
      if (data.passed) toast.success(`Passed with ${data.score}% — module completed!`);
      else toast.error(`Scored ${data.score}%. ${data.pass_mark}% needed to pass. Try again.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const retake = () => { setResult(null); setAnswers({}); };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="module-detail">
      <Link to="/training" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-mlily-blue" data-testid="back-to-training">
        <ArrowLeft className="w-4 h-4" /> Back to training
      </Link>

      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Module {m.order}</span>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 mt-1">{m.title}</h1>
      </div>

      {m.image_url && (
        <img src={m.image_url} alt={m.title} className="w-full h-56 object-cover rounded-xl border border-slate-200" />
      )}

      <Card className="p-6">
        <h3 className="font-heading font-bold text-slate-900 mb-2">Overview</h3>
        <p className="text-slate-700 leading-relaxed whitespace-pre-line">{m.explanation}</p>
      </Card>

      {m.video_url && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3 text-slate-900 font-semibold">
            <PlayCircle className="w-5 h-5 text-violet-500" /> Module video
          </div>
          <div className="aspect-video rounded-lg overflow-hidden bg-black">
            <iframe title="module-video" src={toEmbedUrl(m.video_url)} className="w-full h-full" allowFullScreen />
          </div>
        </Card>
      )}

      {m.common_mistakes?.length > 0 && (
        <Card className="p-6 border-amber-200 bg-amber-50/50">
          <div className="flex items-center gap-2 mb-3 text-amber-700 font-heading font-bold">
            <AlertTriangle className="w-5 h-5" /> Common mistakes
          </div>
          <ul className="space-y-2">
            {m.common_mistakes.map((c, i) => (
              <li key={i} className="text-sm text-amber-900 flex gap-2"><span>•</span>{c}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Quiz */}
      {quiz.length > 0 && (
        <Card className="p-6" data-testid="quiz-section">
          <h3 className="font-heading font-bold text-slate-900 text-lg mb-1">Quiz</h3>
          <p className="text-sm text-slate-500 mb-5">Pass mark: {m.pass_mark}%. You can retake as many times as you like.</p>

          {result ? (
            <div className="text-center py-6" data-testid="quiz-result">
              {result.passed ? (
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              ) : (
                <XCircle className="w-14 h-14 text-red-500 mx-auto" />
              )}
              <div className="font-heading text-3xl font-extrabold mt-3">{result.score}%</div>
              <p className="text-slate-500 mt-1">{result.correct} of {result.total} correct</p>
              <p className={result.passed ? "text-emerald-600 font-semibold mt-2" : "text-red-600 font-semibold mt-2"}>
                {result.passed ? "Module completed ✅" : `Not passed — ${result.pass_mark}% required`}
              </p>
              <div className="flex gap-3 justify-center mt-5">
                <Button variant="outline" onClick={retake} data-testid="retake-quiz-btn">Retake quiz</Button>
                <Link to="/training"><Button className="bg-mlily-blue hover:bg-mlily-bluehover" data-testid="back-training-btn">Back to training</Button></Link>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {quiz.map((q, qi) => (
                <div key={qi} data-testid={`quiz-q-${qi}`}>
                  <div className="font-medium text-slate-900 mb-3">{qi + 1}. {q.question}</div>
                  <RadioGroup value={answers[qi]?.toString()} onValueChange={(v) => setAnswers((a) => ({ ...a, [qi]: parseInt(v) }))}>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                        <RadioGroupItem value={oi.toString()} id={`q${qi}o${oi}`} data-testid={`quiz-q${qi}-opt${oi}`} />
                        <Label htmlFor={`q${qi}o${oi}`} className="cursor-pointer font-normal flex-1">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
              <Button disabled={!allAnswered || submitting} onClick={submit}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="submit-quiz-btn">
                {submitting ? "Submitting…" : "Submit quiz & complete module"}
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
